import { DEFAULT_DATE_TIME_RANGE } from '@modules/common/components/DateRangePicker/constants';
import { mapToDateTimeRange } from '@modules/common/utils';
import { useGetPromptPosition } from '@modules/oracle/hooks/useGetPromptPosition';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { EqlQueryType } from 'generated/graphql';
import { useMemo } from 'react';
import { WisdomEventProps } from 'v2/analytics/events';
import { GroupBy } from 'v2/core/entities';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { parseEQL } from 'v2/core/repository/eql/parser';
import { EQLProps, ExpressionType } from 'v2/core/repository/eql/types';
import { useGetUser } from 'v2/core/services/user';

interface useGetOracleStepAnalyticEventInputsParams {
  promptID: string;
  stepID: string;
}

export function useGetOracleStepAnalyticEventInputs({
  promptID,
  stepID,
}: useGetOracleStepAnalyticEventInputsParams): Omit<
  WisdomEventProps,
  'eventName'
> {
  const {
    tenant: { id: tenantID, timezone },
  } = useGetUser();
  const sessionID = useOracleSearchStore(({ data }) => data.sessionID);
  const threadPosition = useGetPromptPosition(promptID);
  const isLastMessage = useOracleSearchStore(
    ({ data }) => promptID === data.currentPromptID,
  );

  const promptText = useOracleSearchStore(
    ({ data }) => data.promptByID?.[promptID].question,
  );
  const assistantResponse = useOracleSearchStore(
    ({ data }) => data.promptByID?.[promptID].assistantResponse,
  );
  const finalAnswerTokensV2 = assistantResponse?.answer?.tokensV2;
  const stepTitle = assistantResponse?.steps?.find(
    ({ id }) => id === stepID,
  )?.question;
  const stepAnswer = assistantResponse?.stepAnswerByID?.[stepID];
  const assistantResponseTokenV2List = stepAnswer?.isSameAsAnswer
    ? finalAnswerTokensV2
    : stepAnswer?.tokensV2;
  const eqlQuery = assistantResponseTokenV2List?.find((token) => token.eql?.eql)
    ?.eql?.eql;

  const stepType =
    eqlQuery?.properties.type === EqlQueryType.Aggregates
      ? 'quantify'
      : 'summary';

  const { data: sourceDetailsMap } = useGetSourceDetailsMap();

  const whereClause = useMemo(() => {
    if (eqlQuery?.properties.whereClause && sourceDetailsMap) {
      const eql = eqlQuery.properties.whereClause.startsWith('select')
        ? eqlQuery.properties.whereClause
        : `select ${eqlQuery?.properties.type.toLowerCase()} where ${
            eqlQuery.properties.whereClause
          }`;

      return parseEQL(eql, sourceDetailsMap, timezone).whereClause ?? undefined;
    }
  }, [
    eqlQuery?.properties.whereClause,
    eqlQuery?.properties.type,
    sourceDetailsMap,
    timezone,
  ]);

  const duration = useMemo(() => {
    if (eqlQuery?.properties.duration) {
      const duration = { ...eqlQuery.properties.duration };
      if (duration.startAfter) {
        duration.startAfter = duration.startAfter * 1000;
      }
      if (duration.endBefore) {
        duration.endBefore = duration.endBefore * 1000;
      }

      return mapToDateTimeRange(duration);
    } else {
      return DEFAULT_DATE_TIME_RANGE;
    }
  }, [eqlQuery?.properties.duration]);

  const eqlProps: Omit<EQLProps, 'sourceDetailsMap'> = {
    expressionType:
      eqlQuery?.properties.type === EqlQueryType.Aggregates
        ? ExpressionType.Aggregates
        : ExpressionType.Records,
    whereClause,
    dateRange: duration as EQLProps['dateRange'],
    groupBy: eqlQuery?.properties.groupBy as GroupBy,
    aggregatePeriod: eqlQuery?.properties.aggregatePeriod,
  };
  const { eql } = useGetEQLExpression({
    tenantID,
    eqlProps,
  });

  return {
    sessionID,
    responseID: promptID,
    threadPosition,
    isLastMessage,
    stepTitle,
    stepEql: stepType === 'quantify' ? undefined : eql,
    stepType,
    searchText: promptText,
    stepEqlProps: eqlQuery?.properties,
  };
}
